/**
 * DialogHost — randează dialogurile cerute prin `confirmDialog` / `alertDialog`
 * (vezi stores/dialog.ts). Unul câte unul, în ordinea cererii.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '@/components/Modal';
import { useDialogStore } from '@/stores/dialog';

export default function DialogHost() {
  const { t } = useTranslation();
  const current = useDialogStore((s) => s.queue[0]);
  const settle = useDialogStore((s) => s.settle);
  const primaryRef = useRef<HTMLButtonElement>(null);

  // Focus pe butonul principal: Enter confirmă, Escape (din Modal) anulează.
  useEffect(() => {
    if (current) primaryRef.current?.focus();
  }, [current]);

  if (!current) return null;

  const isConfirm = current.kind === 'confirm';
  const close = (ok: boolean) => settle(current.id, ok);

  return (
    <Modal
      open
      onClose={() => close(false)}
      maxWidth="max-w-md"
      title={
        <h2 className="display-title-sm">
          {current.title ?? t(isConfirm ? 'dialog.confirm_title' : 'dialog.alert_title')}
        </h2>
      }
    >
      <div data-testid="app-dialog">
        <p className="text-sm text-[var(--text)] whitespace-pre-line">{current.message}</p>
        <div className="flex gap-2 justify-end mt-5">
          {isConfirm && (
            <button
              type="button"
              className="btn-outline text-sm"
              onClick={() => close(false)}
              data-testid="app-dialog-cancel"
            >
              {t('common.cancel')}
            </button>
          )}
          <button
            ref={primaryRef}
            type="button"
            className="btn-primary text-sm"
            style={
              current.danger
                ? { background: 'var(--danger)', borderColor: 'var(--danger)' }
                : undefined
            }
            onClick={() => close(true)}
            data-testid="app-dialog-ok"
          >
            {current.confirmLabel ?? t(isConfirm ? 'common.continue' : 'common.close')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
